import {
    createStyles,
    Fab,
    Modal,
    Paper,
    Theme,
    Typography,
    withStyles,
    WithStyles
} from "@material-ui/core";
import { CheckCircleOutline } from "@material-ui/icons";
import React, { useEffect, useState } from "react";
import NumberFormat from "react-number-format";
import { connect } from "react-redux";
import { AnyAction, bindActionCreators, Dispatch } from "redux";
import { IParcelDetail } from "../../models/parcel/IParcelDetail";
import { IAppState } from "../../store";
import * as ParcelBidStore from "../../store/ParcelBidStore";
import * as ParcelStore from "../../store/ParcelStore";
import * as UserStore from "../../store/UserStore";
import { DateTime } from "luxon";
import { trackWithStackAdaptPixel } from "../../hooks/useStackAdaptPixel";
import Constants from "../../models/utility/Constants";
import { useGetConfigQuery } from "../../api/configApiSlice";

type ParcelClosedDialogProps = ParcelStore.IParcelStoreState &
    ParcelBidStore.IParcelBidStoreState &
    UserStore.IUserStoreState &
    typeof ParcelStore.actionCreators &
    typeof ParcelBidStore.actionCreators &
    typeof UserStore.actionCreators &
    WithStyles<typeof styles>;

const styles = (theme: Theme) =>
    createStyles({
        body: {
            [theme.breakpoints.down("xs")]: { padding: theme.spacing(2) },
            alignItems: "center",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            padding: theme.spacing(4),
            textAlign: "center"
        },
        bodyText: {
            [theme.breakpoints.down("xs")]: { fontSize: 20 },
            color: theme.palette.text.secondary,
            lineHeight: 1.5,
            marginBottom: theme.spacing(3),
            textAlign: "center"
        },
        bodyTextAmount: {
            fontWeight: "bold",
            textAlign: "center"
        },
        errorText: { color: theme.palette.error.main },
        fabButton: {
            height: theme.spacing(4),
            paddingLeft: theme.spacing(5),
            paddingRight: theme.spacing(5)
        },
        fabButtonWrapper: {
            [theme.breakpoints.down("xs")]: {
                marginBottom: theme.spacing(1.5)
            },
            marginBottom: theme.spacing(3)
        },
        header: {
            borderBottom: `8px solid ${theme.palette.primary.main}`,
            padding: theme.spacing(2),
            textAlign: "center",
            width: "100%"
        },
        headerIcon: {
            position: "relative",
            top: 3
        },
        headerText: {
            [theme.breakpoints.down("xs")]: { fontSize: 20 },
            color: theme.palette.primary.main,
            fontWeight: "bold",
            margin: "0 auto"
        },
        paper: {
            backgroundColor: theme.palette.background.paper,
            boxShadow: theme.shadows[5],
            margin: theme.spacing(2),
            maxWidth: 500,
            outline: "none",
            width: "100%"
        },
        statusIcon: {
            [theme.breakpoints.down("xs")]: { fontSize: 40 },
            fontSize: 48
        },
        subNote: {
            fontSize: 18,
            fontStyle: "italic"
        },
        successText: {
            color: theme.palette.success.main,
            fontWeight: "bold"
        }
    });

const renderFinalBidAmount = (parcel: IParcelDetail) => (
    <NumberFormat
        value={parcel.lastAcceptedBid}
        thousandSeparator={true}
        prefix="$"
        decimalScale={0}
        fixedDecimalScale={true}
        displayType="text"
    />
);

// After a parcel closes, if it has closed less than this many minutes ago, show the parcel in the dialog
const PARCEL_CLOSE_THRESHOLD = 1;

function ParcelClosedDialog({
    classes,
    parcelBidDict,
    parcels,
    user
}: ParcelClosedDialogProps) {
    const [unacknowledgedWonParcels, setUnacknowledgedWonParcels] = useState<
        IParcelDetail[]
    >([]);
    const [acknowledgedWonParcels, setAcknowledgedWonParcels] = useState<
        string[]
    >([]);

    const visible = unacknowledgedWonParcels.length > 0;

    const auctionContainsEnBloc = parcels.some((p) => p.enBloc);

    const getConfigQuery = useGetConfigQuery();

    useEffect(() => {
        if (user && parcels.length > 0) {
            const newUnacknowledgedWonParcels = parcels.filter((parcel) => {
                if (
                    parcel.isCompleted &&
                    Math.abs(
                        DateTime.fromISO(parcel.auctionEndDate)
                            .diffNow("minutes")
                            .as("minutes")
                    ) < PARCEL_CLOSE_THRESHOLD &&
                    acknowledgedWonParcels.indexOf(parcel.parcelId) === -1
                ) {
                    const parcelBids = parcelBidDict[parcel.parcelId];
                    const isUserHighestBidder =
                        parcelBids?.length > 0 &&
                        parcelBids[parcelBids.length - 1].userId === user.id;
                    return isUserHighestBidder;
                }
                return false;
            });

            newUnacknowledgedWonParcels.forEach((parcel) => {
                trackWithStackAdaptPixel(
                    getConfigQuery.data?.stackAdaptPixelId,
                    {
                        eventName: Constants.StackAdaptEvents.PlaceHighestBid,
                        bidAmount: parcel.lastAcceptedBid,
                        parcelName: parcel.name,
                        parcelId: parcel.parcelId
                    }
                );
            });

            setUnacknowledgedWonParcels(newUnacknowledgedWonParcels);
        }
    }, [
        parcels,
        parcelBidDict,
        user,
        acknowledgedWonParcels,
        getConfigQuery.data?.stackAdaptPixelId
    ]);

    if (!visible) {
        return null;
    }

    const closeDialog = () => {
        setAcknowledgedWonParcels(
            unacknowledgedWonParcels.map((p) => p.parcelId)
        );
        setUnacknowledgedWonParcels([]);
    };

    return (
        <Modal open={visible} onClose={closeDialog} className={classes.body}>
            <Paper className={classes.paper} elevation={2}>
                <div className={classes.header}>
                    <Typography
                        component="h1"
                        variant="h5"
                        className={classes.headerText}
                    >
                        <CheckCircleOutline className={classes.headerIcon} />
                        &nbsp;Parcel Closed
                    </Typography>
                </div>
                <div className={classes.body}>
                    {/* Congralutatory Text */}
                    <Typography
                        component="div"
                        variant="h5"
                        className={classes.bodyText}
                    >
                        <div>
                            You were the highest bidder for the following
                            parcel:
                        </div>
                        <div className={classes.bodyTextAmount}>
                            {unacknowledgedWonParcels.map((parcel) => {
                                return (
                                    <div key={parcel.parcelId}>
                                        {renderFinalBidAmount(parcel)} on{" "}
                                        {parcel.name}
                                    </div>
                                );
                            })}
                        </div>
                        {auctionContainsEnBloc &&
                            !Array.from(unacknowledgedWonParcels).some(
                                (p) => p.enBloc
                            ) && (
                                <div className={classes.subNote}>
                                    If you meet the requirements outlined on the
                                    website, you will be eligible to bid on the
                                    En Bloc parcel.
                                </div>
                            )}
                    </Typography>

                    {/* Close Button */}
                    <div>
                        <Fab
                            className={classes.fabButton}
                            variant="extended"
                            color="primary"
                            onClick={closeDialog}
                        >
                            Close
                        </Fab>
                    </div>
                </div>
            </Paper>
        </Modal>
    );
}

const reduxState = (state: IAppState) =>
    Object.assign(
        {},
        state.parcelStoreState,
        state.parcelBidStoreState,
        state.userStoreState
    );
const reduxDispatch = (dispatch: Dispatch<AnyAction>) =>
    bindActionCreators(
        Object.assign(
            {},
            ParcelBidStore.actionCreators,
            ParcelStore.actionCreators,
            UserStore.actionCreators
        ),
        dispatch
    );

export default withStyles(styles)(
    connect(reduxState, reduxDispatch)(ParcelClosedDialog)
);
